import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Calendar as CalendarIcon, Download, Package } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { StatsRow } from '../components/admin/StatsRow';
import { BookingCard } from '../components/admin/BookingCard';
import { exportBookingsToCsv } from '../lib/exportBookings';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [consentFilter, setConsentFilter] = useState('all'); // 'all' | 'consenting' | 'no-consent'

  const fetchBookings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/bookings`, { withCredentials: true });
      setBookings(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const stats = useMemo(() => {
    const pending = bookings.filter(b => b.status === 'pending').length;
    const depositPaid = bookings.filter(b => b.status === 'deposit_paid').length;
    const totalRevenue = bookings
      .filter(b => b.payment?.payment_status === 'paid')
      .reduce((sum, b) => sum + (b.payment?.amount || 0), 0);
    return {
      total: bookings.length,
      pending,
      depositPaid,
      totalRevenueFormatted: totalRevenue.toLocaleString(),
    };
  }, [bookings]);

  const filteredBookings = useMemo(() => {
    if (consentFilter === 'consenting') return bookings.filter(b => b.media_consent === true);
    if (consentFilter === 'no-consent') return bookings.filter(b => !b.media_consent);
    return bookings;
  }, [bookings, consentFilter]);

  const consentCounts = useMemo(() => ({
    all: bookings.length,
    consenting: bookings.filter(b => b.media_consent === true).length,
    noConsent: bookings.filter(b => !b.media_consent).length,
  }), [bookings]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const handleExport = () => {
    const ok = exportBookingsToCsv(filteredBookings, consentFilter);
    if (ok) {
      toast.success(`Exported ${filteredBookings.length} booking${filteredBookings.length !== 1 ? 's' : ''} to CSV`);
    } else {
      toast.info('Nothing to export — current filter has no bookings');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F1E8] p-6 flex items-center justify-center">
        <p className="text-[#8B7355] font-serif text-xl">Loading bookings...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F1E8] py-12" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-5xl font-serif text-[#8B7355] mb-2 italic">Admin Dashboard</h1>
            <p className="text-lg text-[#8B7355]/80">Welcome back, {user?.name || 'Admin'}!</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handleExport}
              variant="outline"
              className="border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961] hover:text-white font-serif"
              data-testid="admin-export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button
              onClick={() => navigate('/admin/manage-products')}
              variant="outline"
              className="border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961] hover:text-white font-serif"
              data-testid="admin-manage-products-btn"
            >
              <Package className="w-4 h-4 mr-2" />
              Packages
            </Button>
            <Button
              onClick={() => navigate('/admin/calendar')}
              variant="outline"
              className="border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961] hover:text-white font-serif"
              data-testid="admin-calendar-btn"
            >
              <CalendarIcon className="w-4 h-4 mr-2" />
              Calendar
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-[#8B7355] text-[#8B7355] hover:bg-[#8B7355] hover:text-white font-serif"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        <StatsRow stats={stats} />

        {/* Consent filter pills */}
        <div className="mb-6 flex flex-wrap gap-2 items-center" data-testid="consent-filter-row">
          <span className="text-sm text-[#8B7355]/70 font-serif mr-1">Show:</span>
          <button
            type="button"
            onClick={() => setConsentFilter('all')}
            className={`px-4 py-2 text-sm font-serif rounded-full transition-colors ${
              consentFilter === 'all'
                ? 'bg-[#8B7355] text-white'
                : 'bg-white border border-[#8B7355]/30 text-[#8B7355] hover:border-[#8B7355]'
            }`}
            data-testid="consent-filter-all"
          >
            All Bookings ({consentCounts.all})
          </button>
          <button
            type="button"
            onClick={() => setConsentFilter('consenting')}
            className={`px-4 py-2 text-sm font-serif rounded-full transition-colors ${
              consentFilter === 'consenting'
                ? 'bg-green-600 text-white'
                : 'bg-white border border-green-600/30 text-green-700 hover:border-green-600'
            }`}
            data-testid="consent-filter-consenting"
          >
            Can Post on Social ({consentCounts.consenting})
          </button>
          <button
            type="button"
            onClick={() => setConsentFilter('no-consent')}
            className={`px-4 py-2 text-sm font-serif rounded-full transition-colors ${
              consentFilter === 'no-consent'
                ? 'bg-amber-600 text-white'
                : 'bg-white border border-amber-600/30 text-amber-700 hover:border-amber-600'
            }`}
            data-testid="consent-filter-no-consent"
          >
            Private Only ({consentCounts.noConsent})
          </button>
        </div>

        <div className="space-y-6">
          {filteredBookings.map((booking) => (
            <BookingCard key={booking.id} booking={booking} />
          ))}

          {filteredBookings.length === 0 && (
            <Card>
              <CardContent className="p-12 text-center">
                <p className="text-[#8B7355]/70 font-serif text-lg">
                  {bookings.length === 0
                    ? 'No bookings yet'
                    : 'No bookings match this filter'}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
